import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";

export default function PropertyExcelImportModal({ open, onClose, onDone }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [approved, setApproved] = useState(false);
    const [activationMode, setActivationMode] = useState("inactive");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setFile(null);
        setPreview(null);
        setApproved(false);
        setActivationMode("inactive");
        setResult(null);
        setError("");
    }, [open]);

    const runPreview = async () => {
        if (!file) return;
        setLoading(true);
        setError("");
        try {
            const body = new FormData();
            body.append("file", file);
            const { data } = await api.post("/properties/import/preview", body, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const commit = async () => {
        setLoading(true);
        setError("");
        try {
            const body = new FormData();
            body.append("file", file);
            body.append("preview_sha256", preview.file_sha256);
            body.append("reconciliation_approved", String(approved));
            body.append("activation_mode", activationMode);
            const { data } = await api.post("/properties/import/commit", body, { headers: { "Content-Type": "multipart/form-data" } });
            setResult(data);
            onDone?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} title="Importar catálogo de viviendas" subtitle="Proceso controlado: vista previa, conciliación y commit idempotente.">
            <div className="space-y-4" data-testid="property-excel-import-modal">
                {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
                {result ? (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-900"><CheckCircle2 className="inline mr-2" size={18} />Lote completado: {result.created} creadas, {result.updated} actualizadas, {result.activated} activas y {result.inactive} inactivas.</div>
                        <button className="zentra-btn-primary w-full justify-center" onClick={onClose}>Cerrar</button>
                    </div>
                ) : !preview ? (
                    <div className="space-y-4">
                        <label className="block rounded-xl border-2 border-dashed border-zentra-border p-8 text-center cursor-pointer hover:border-zentra-accent">
                            <FileSpreadsheet className="mx-auto text-zentra-muted mb-2" />
                            <span className="text-sm font-medium">{file?.name || "Selecciona BD NUEVA.xlsx"}</span>
                            <input className="hidden" type="file" accept=".xlsx" onChange={(event) => setFile(event.target.files?.[0] || null)} />
                        </label>
                        <p className="text-xs text-zentra-muted">La vista previa no escribe en MongoDB ni realiza geocodificación.</p>
                        <button className="zentra-btn-primary w-full justify-center" disabled={!file || loading} onClick={runPreview}>{loading ? "Analizando…" : "Generar vista previa"}</button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <Metric label="Viviendas" value={preview.summary.total} />
                            <Metric label="Códigos únicos" value={preview.summary.unique_codes} />
                            <Metric label="Sin dirección" value={preview.summary.missing_address} warning />
                            <Metric label="Sin población" value={preview.summary.missing_city} warning />
                        </div>
                        <div className="rounded-lg border border-zentra-border p-3 text-sm space-y-1">
                            <p className="font-semibold">Conciliación de catálogos</p>
                            <p>{preview.reconciliation.matching_codes.length} códigos coincidentes</p>
                            <p>{preview.reconciliation.catalog_only_codes.length} sólo en Hoja 3</p>
                            <p>{preview.reconciliation.bd_only_codes.length} sólo en BD</p>
                            <p className="text-amber-800 flex gap-2 pt-1"><AlertTriangle size={15} />{preview.summary.formula_cells_total} fórmulas detectadas; sus resultados no se consideran originales automáticamente.</p>
                        </div>
                        <label className="block">
                            <span className="zentra-label">Decisión explícita de activación</span>
                            <select className="zentra-input w-full" value={activationMode} onChange={(event) => setActivationMode(event.target.value)}>
                                <option value="inactive">Importar todas como inactivas</option>
                                <option value="intersection">Activar sólo las coincidentes entre BD y Hoja 3</option>
                                <option value="all_bd">Activar todas las viviendas de BD</option>
                            </select>
                        </label>
                        <label className="flex items-start gap-3 rounded-lg border border-zentra-border p-3 text-sm">
                            <input type="checkbox" className="mt-1" checked={approved} onChange={(event) => setApproved(event.target.checked)} />
                            <span><strong>Apruebo la conciliación mostrada.</strong><br /><span className="text-zentra-muted">El commit usará exactamente el archivo validado mediante SHA-256.</span></span>
                        </label>
                        <div className="rounded-lg bg-zentra-bg p-3 text-xs text-zentra-muted flex gap-2"><ShieldCheck size={16} />No se geocodificará ninguna vivienda durante este commit.</div>
                        <div className="flex justify-end gap-2"><button className="zentra-btn-ghost" onClick={() => setPreview(null)}>Volver</button><button className="zentra-btn-primary" disabled={!approved || loading} onClick={commit}>{loading ? "Importando…" : "Ejecutar commit"}</button></div>
                    </div>
                )}
            </div>
        </Modal>
    );
}

function Metric({ label, value, warning = false }) {
    return <div className={`rounded-lg p-3 ${warning ? "bg-amber-50 text-amber-900" : "bg-zentra-bg text-zentra-ink"}`}><p className="text-xl font-semibold">{value}</p><p className="text-xs opacity-75">{label}</p></div>;
}
